<template>
  <div class="relative">
    <TextEditor
      type="slide-title"
      class="slide-title-editor"
      :id="slideActive.id"
      @update="onUpdateSlideTitle"
      :value="valueHtml"
      :initialValue="initialValueHtml"
      :disabled="isPresentingOrBackstageOrShare"
      :hasBorder="!isPresentingOrBackstageOrShare && isDefaultContent"
      :isTextColourLight="isTextColourLight"
      :maxChars="maxChars"
      :isPresentingDefaultContent="isPresentingDefaultContent"
      @onChangeClientHeight="onChangeClientHeight"
    />
    <AIFloatButton
      class="canvas-ai-button"
      v-if="isInputWithAIFloatButton"
      :class="{'show': isShowingAIFLoatButton}"
      :isButtonVisible="isShowingAIFLoatButton"
      :gaTouchPoint="'Canvas'"
      :style="{'height': inputHeight}"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import TextEditor from './TextEditor.vue'
import SlideApi from '@/api/slide'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import Socket from '@/socket'
import EventBus from '@/eventBus'
import constants from '@/constant'
import AIFloatButton from '@/components-v2/presentation-editor/new-slide/OpenAI/AIFloatButton.vue'

export default {
  components: {
    TextEditor,
    AIFloatButton,
  },
  props: {
    maxChars: {
      type: Number,
      default: 300,
    },
  },
  data() {
    return {
      inputHeight: '121px',
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage', 'isAITestGroup']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      slideGenerator: (state) => state.slideGenerator,
    }),
    defaultTitle() {
      let title = ''
      constants.defaultSlideTitles.forEach((item) => {
        if (this.slideActive.type === item.type) {
          title = item.defaultTitle
        }
      })

      return title
    },
    defaultHtmlContent() {
      const align = this.slideActive.textAlign || 'center'
      return `<p style="text-align: ${align}">${this.defaultTitle}</p>`
    },
    isPresentingOrBackstageOrShare() {
      return this.sharePresentation || this.isPresenting || this.isBackstage
    },
    isPresentingDefaultContent() {
      return this.isDefaultContent && this.isPresentingOrBackstageOrShare && this.slideActive.type !== 'leaderboard'
    },

    transformedTitle() {
      if (!this.slideActive.title) {
        return null
      }
      const align = this.slideActive.textAlign || 'center'
      return `<p style="text-align: ${align}">${this.slideActive.title}</p>`
    },
    titleHtml() {
      return this.slideActive.titleHTML || this.transformedTitle || this.defaultHtmlContent
    },
    initialValueHtml() {
      return this.defaultHtmlContent
    },
    valueHtml() {
      return this.titleHtml
    },
    isCorrectOrderSlideType() {
      return this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'correctOrder'
    },
    isTextColourLight() {
      return this.slideActive.textColour === '#ffffff'
    },
    isDefaultContent() {
      return utils.extractContent(this.valueHtml, true) ===
        utils.extractContent(this.initialValueHtml, true)
    },
    isPickAnswerOrTypeAnswerSlide() {
      return this.slideActive.type === 'pickAnswer' && (!this.slideActive.slideType || this.slideActive.slideType === 'typeAnswer')
    },
    isInputWithAIFloatButton() {
      return (this.isPickAnswerOrTypeAnswerSlide || this.slideActive.type === 'freestyle') && !this.isPresentingOrBackstageOrShare
        && this.isAITestGroup
    },
    inputId() {
      return `mce-editor-slide-title-${this.slideActive.id}`
    },
    isShowingAIFLoatButton() {
      return this.slideGenerator.isAIFloatButtonShowing === this.inputId
    },
  },

  methods: {
    ...mapActions(['updateSlideActiveAction', 'updateSlideByIdAction', 'setSyncLoadingAction', 'addActionToHistory']),

    async onUpdateSlideTitle(type, props) {
      try {
        this.updateSlideByIdAction(props)
        this.setSyncLoadingAction(1)
        const payload = {
          presentationId: this.presentation.id,
          accessCode: this.presentation.uniqueAccessCode,
          socketId: Socket.getSocketId(),
          presenting: this.isPresenting || this.isBackstage,
          ...props,
        }
        this.addHistoryUpdateTitleSlide(payload.title)

        EventBus.$emit(`update-right-column-for-${type}-${props.id}`, payload.title)
        await SlideApi.updateSlideById(props.id, payload)
        this.setSyncLoadingAction(2)
        this._handleTrackGAAfterUpdate()
      } catch (error) {
        this.setSyncLoadingAction(3)
      }
    },

    addHistoryUpdateTitleSlide(title) {
      if (title === this.slideActive.title) {
        return
      }
      this.addActionToHistory({
        type: 'UpdateTitleSlide',
        data: {
          idName: 'title-slide-edit',
          value: this.slideActive.title,
        },
        newData: {
          idName: 'title-slide-edit',
          value: title,
        },
        slideActiveId: this.slideActive.id,
        componentName: 'SlideTitleSettings',
      })
    },

    _handleTrackGAAfterUpdate() {
      const formalizedSlideType = `${this.slideActive.type} ${this.slideActive.slideType}`
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Edit slide title',
        'Canvas',
        formalizedSlideType,
        {
          slideId: this.slideActive.id,
        },
      )
    },

    onChangeClientHeight(height) {
      if (height < 100) {
        this.inputHeight = `${height + 70}px`
      } else {
        this.inputHeight = `${height + 40}px`
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.slide-title-editor {
  min-width: 150px;
  max-height: 270px;
}
.canvas-ai-button {
  &::v-deep.ai-float-button__wrapper {
    @apply absolute top-0 right-2 cursor-pointer z-1 flex items-end;
    .ai-float-button {
      @apply opacity-0 pointer-events-none;
      width: 6rem;
    }
  }
  &.show::v-deep {
    .ai-float-button {
      @apply opacity-100;
      pointer-events: all;
    }
  }
  @media (max-width: $ipad-breakpoint) {
    display: none;
  }
}

</style>
